import React, { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { renderToString } from 'react-dom/server';
import type { RecordProxy } from 'relay-runtime/lib/store/RelayStoreTypes';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import Link from '@atlaskit/link';
import { fg } from '@atlassian/jira-feature-gating';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';

import {
	PARENT_GQL_FIELD,
	PRIORITY_GQL_FIELD,
	USER_PICKER_GQL_FIELD,
	DATE_PICKER_GQL_FIELD,
	TEXT_GQL_FIELD,
	LABEL_GQL_FIELD,
	RICH_TEXT_GQL_FIELD,
	NUMBER_GQL_FIELD,
	MULTI_CHECKBOXES_CF_TYPE,
	STATUS_GQL_FIELD,
	DATE_TIME_GQL_FIELD,
	USER_CF_TYPE,
	AFFECTED_SERVICES_GQL_FIELD,
	CMDB_GQL_FIELD,
	WATCHES_GQL_FIELD,
	PEOPLE_GQL_FIELD,
	RESPONDERS_GQL_FIELD,
	FORGE_STRING_GQL_FIELD,
	FORGE_OBJECT_GQL_FIELD,
	FORGE_USER_GQL_FIELD,
	FORGE_NUMBER_GQL_FIELD,
	FORGE_STRINGS_GQL_FIELD,
	FORGE_GROUP_GQL_FIELD,
	FORGE_GROUPS_GQL_FIELD,
	FLAG_GQL_FIELD,
	FORGE_DATETIME_GQL_FIELD,
	ISSUE_LINK_GQL_FIELD,
	FORGE_USERS_GQL_FIELD,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { JiraIssuefieldvalueAri } from '@atlassian/ari/jira/issuefieldvalue';
import RelayDataID from '@atlassian/relay-data-id';
import { convertAdfToText } from '@atlassian/jira-rich-content/src/common/convert-adf-to-text/index.tsx';
import { JiraIssueAri } from '@atlassian/ari/jira/issue';
import type { UpdaterData } from '@atlassian/jira-bulk-operations-common/src/common/controllers/use-bulk-operations-pub-sub/types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { JiraBulkEditMultiSelectFieldOptions } from '@atlassian/jira-relay/src/__generated__/useBulkMutation.graphql';

const getSidebarWidthCache = (() => {
	let sidebarWidthCache: ReturnType<typeof createLocalStorageProvider>;
	return () => {
		if (__SERVER__) return;

		if (!sidebarWidthCache) {
			sidebarWidthCache = createLocalStorageProvider('bulk_operations_sidebar_ratio');
		}

		return sidebarWidthCache;
	};
})();

const deDuplicateEdges = (edges: RecordProxy<{}>[]) => {
	const seenEntities = new Set<string>();
	const uniqueEdges = edges.filter((edge) => {
		const entityName = String(edge?.getLinkedRecord('node')?.getValue('name'));
		if (seenEntities.has(entityName)) return false;
		seenEntities.add(entityName);
		return true;
	});

	return uniqueEdges.sort((edgeA, edgeB) => {
		const entityA = String(edgeA?.getLinkedRecord('node')?.getValue('name'));
		const entityB = String(edgeB?.getLinkedRecord('node')?.getValue('name'));
		return entityA.localeCompare(entityB);
	});
};

export const updateRecords = (
	entityConnectionRecord: RecordProxy<{}>,
	records: RecordProxy<{}>[],
): void => {
	entityConnectionRecord?.setLinkedRecords(records, 'edges');
	entityConnectionRecord?.setValue(records.length, 'totalCount');
};

export const runMultiSelectOptions = (
	multiSelectFieldOption: JiraBulkEditMultiSelectFieldOptions | undefined,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formFieldData: any,
	storeField: RecordProxy<{}>,
	store: RecordSourceSelectorProxy<{}>,
	options: {
		entityConnection: {
			name: string;
			args?: {
				[key: string]: string | number | boolean;
			};
		};
		entityType: string;
		// optional, defaults to the entityType+"Edge"
		entityEdgeType?: string;
		values?: { [key: string]: string };
	},
): void => {
	const { entityConnection, entityType, entityEdgeType, values } = options;

	const entityConnectionRecord = storeField.getLinkedRecord(
		entityConnection.name,
		entityConnection?.args ?? null,
	);
	if (!entityConnectionRecord) return;

	switch (multiSelectFieldOption) {
		case 'ADD':
		case 'REPLACE': {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const newEdges = formFieldData.map((entityData: any) => {
				const entity = store.create(uuid(), entityType);
				if (values) {
					Object.entries(values).forEach(([key, value]) => {
						entity.setValue(entityData?.[value], key);
					});
				}
				const edge = store.create(uuid(), entityEdgeType ?? `${entityType}Edge`);
				edge.setLinkedRecord(entity, 'node');
				return edge;
			});
			const existingEdges: RecordProxy<{}>[] =
				entityConnectionRecord.getLinkedRecords('edges') || [];
			const combinedEdges = [
				...(multiSelectFieldOption === 'ADD' ? existingEdges : []),
				...newEdges,
			];

			const uniqueEdges = deDuplicateEdges(combinedEdges);

			updateRecords(entityConnectionRecord, uniqueEdges);
			break;
		}

		case 'REMOVE': {
			const existingEdges: RecordProxy<{}>[] =
				entityConnectionRecord.getLinkedRecords('edges') || [];
			const entitiesToRemove = formFieldData.map(
				(entityData: { label: string }) => entityData.label,
			);
			const updatedEdges = existingEdges.filter((edge) => {
				const entityName = edge?.getLinkedRecord('node')?.getValue('name');
				return !entitiesToRemove.includes(entityName);
			});
			updateRecords(entityConnectionRecord, updatedEdges);
			break;
		}

		case 'REMOVE_ALL': {
			updateRecords(entityConnectionRecord, []);
			break;
		}

		default:
			break;
	}
};

const STORAGE_KEY = 'sidebar_ratio';

export const getStoredRatio = (defaultValue: number) =>
	getSidebarWidthCache()?.get(STORAGE_KEY) ?? defaultValue;

export const setStoredRatio = (ratio: number) => getSidebarWidthCache()?.set(STORAGE_KEY, ratio);

export type UpdatingGQLField = {
	fieldId: string;
	gqlType: string;
	type: string;
};

type UpdaterProps = {
	onUpdate?: (data: UpdaterData) => (store: RecordSourceSelectorProxy) => void;
};

export const useOnUpdate = (issueFields: UpdatingGQLField[]) => {
	const onUpdate = useCallback<Required<UpdaterProps>['onUpdate']>(
		(data) => (store) => {
			try {
				data.forEach(({ ariIssueIds, formData, extraData, bulkOperationType }) => {
					issueFields.forEach((field) => {
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
						const formFieldData = formData[field.fieldId] as any;
						if (formFieldData !== undefined) {
							ariIssueIds.forEach((ariIssueId) => {
								const { siteId, issueId } = JiraIssueAri.parse(ariIssueId);
								const ariIssueFieldId = JiraIssuefieldvalueAri.create({
									siteId,
									issueId,
									fieldId: field.fieldId,
								}).toString();
								const relayDataId = RelayDataID(
									{
										id: ariIssueFieldId,
									},
									field.gqlType,
								);
								if (relayDataId) {
									const storeField = store.get(relayDataId);
									if (storeField) {
										switch (field.gqlType) {
											case PRIORITY_GQL_FIELD: {
												const newRecord = store.create(uuid(), 'JiraPriority');
												newRecord.setValue(formFieldData.iconUrl, 'iconUrl');
												newRecord.setValue(formFieldData.label, 'name');
												storeField.setLinkedRecord(newRecord, 'priority');
												break;
											}
											case USER_PICKER_GQL_FIELD: {
												// TODO: there is a bug with Automatic not being picked up in NIN view
												// This needs to be fixed separately https://hello.jira.atlassian.cloud/browse/DEE-6423
												if (formFieldData === null) {
													storeField.setValue(null, 'user');
													return;
												}
												const newRecord = store.create(uuid(), 'AtlassianAccountUser');
												if (formFieldData.accountId) {
													newRecord.setValue(formFieldData.accountId, 'accountId');
													newRecord.setValue(formFieldData.displayName, 'name');
													newRecord.setValue(formFieldData.avatarUrls?.['48x48'], 'picture');
												} else {
													newRecord.setValue(formFieldData.id, 'accountId');
													newRecord.setValue(formFieldData.name, 'name');
													newRecord.setValue(formFieldData.avatarUrl, 'picture');
												}
												newRecord.setValue('active', 'accountStatus');
												storeField.setLinkedRecord(newRecord, 'user');
												break;
											}
											case DATE_PICKER_GQL_FIELD: {
												storeField.setValue(formFieldData, 'date');
												break;
											}
											case NUMBER_GQL_FIELD: {
												storeField.setValue(formFieldData === '' ? null : formFieldData, 'number');
												break;
											}
											case TEXT_GQL_FIELD: {
												storeField.setValue(formFieldData, 'text');
												break;
											}
											case PARENT_GQL_FIELD: {
												if (formFieldData === null) {
													storeField.setValue(null, 'parentIssue');
													return;
												}
												const parentIssue = store.create(uuid(), 'JiraIssue');
												parentIssue.setValue(formFieldData.key, 'key');

												const summaryField = store.create(uuid(), 'JiraSingleLineTextField');
												summaryField.setValue(formFieldData.fields.summary, 'text');
												parentIssue.setLinkedRecord(summaryField, 'summaryField');

												const issueColorField = store.create(uuid(), 'JiraColorField');
												const colorField = store.create(uuid(), 'JiraColor');
												colorField.setValue(formFieldData.color, 'colorKey');
												issueColorField.setLinkedRecord(colorField, 'color');
												parentIssue.setLinkedRecord(issueColorField, 'issueColorField');

												const issueTypeField = store.create(uuid(), 'JiraIssueTypeField');
												const issueType = store.create(uuid(), 'JiraIssueType');
												const avatar = store.create(uuid(), 'JiraAvatar');
												avatar.setValue(formFieldData.fields.issuetype.iconUrl, 'xsmall');
												issueType.setValue(formFieldData.fields.issuetype.name, 'name');
												issueType.setLinkedRecord(avatar, 'avatar');
												issueTypeField.setLinkedRecord(issueType, 'issueType');
												parentIssue.setLinkedRecord(issueTypeField, 'issueTypeField');

												storeField.setLinkedRecord(parentIssue, 'parentIssue');
												break;
											}
											case LABEL_GQL_FIELD: {
												const multiSelectFieldOption = extraData?.[field.fieldId];

												if (expVal('sunsetting_bulk_edit', 'isEnabled', false)) {
													runMultiSelectOptions(
														multiSelectFieldOption,
														formFieldData,
														storeField,
														store,
														{
															entityConnection: {
																name: 'selectedLabelsConnection',
																args: {
																	first: 1000,
																},
															},
															entityType: 'JiraLabel',
															values: {
																name: 'label',
															},
														},
													);
												} else {
													if (
														multiSelectFieldOption === 'ADD' ||
														multiSelectFieldOption === 'REPLACE'
													) {
														const newEdges = formFieldData.map((labelData: { value: string }) => {
															const label = store.create(uuid(), 'JiraLabel');
															label.setValue(labelData.value, 'name');
															const edge = store.create(uuid(), 'JiraLabelEdge');
															edge.setLinkedRecord(label, 'node');
															return edge;
														});
														const selectedLabelsConnection = storeField.getLinkedRecord(
															'selectedLabelsConnection',
															{ first: 1000 },
														);
														const existingEdges: RecordProxy<{}>[] =
															selectedLabelsConnection?.getLinkedRecords('edges') || [];
														const combinedEdges = [
															...(multiSelectFieldOption === 'ADD' ? existingEdges : []),
															...newEdges,
														];
														const seenLabels = new Set<string>();
														const uniqueEdges = combinedEdges.filter((edge) => {
															const labelName = edge.getLinkedRecord('node').getValue('name');
															if (seenLabels.has(labelName)) {
																return false;
															}
															seenLabels.add(labelName);
															return true;
														});
														uniqueEdges.sort((edgeA, edgeB) => {
															const labelA = edgeA.getLinkedRecord('node').getValue('name');
															const labelB = edgeB.getLinkedRecord('node').getValue('name');
															return labelA.localeCompare(labelB);
														});
														selectedLabelsConnection?.setLinkedRecords(uniqueEdges, 'edges');
														selectedLabelsConnection?.setValue(uniqueEdges.length, 'totalCount');
														break;
													}

													if (multiSelectFieldOption === 'REMOVE') {
														const selectedLabelsConnection = storeField.getLinkedRecord(
															'selectedLabelsConnection',
															{ first: 1000 },
														);
														if (selectedLabelsConnection) {
															const existingEdges: RecordProxy<{}>[] =
																selectedLabelsConnection.getLinkedRecords('edges') || [];
															const labelsToRemove = formFieldData.map(
																(labelData: { value: string }) => labelData.value,
															);
															const updatedEdges = existingEdges.filter((edge) => {
																const labelName = edge?.getLinkedRecord('node')?.getValue('name');
																return !labelsToRemove.includes(labelName);
															});
															selectedLabelsConnection.setLinkedRecords(updatedEdges, 'edges');
															selectedLabelsConnection.setValue(updatedEdges.length, 'totalCount');
														}
														break;
													}

													if (multiSelectFieldOption === 'REMOVE_ALL') {
														const selectedLabelsConnection = storeField.getLinkedRecord(
															'selectedLabelsConnection',
															{ first: 1000 },
														);
														if (selectedLabelsConnection) {
															selectedLabelsConnection.setLinkedRecords([], 'edges');
															selectedLabelsConnection.setValue(0, 'totalCount');
														}
														break;
													}
												}

												break;
											}
											case DATE_TIME_GQL_FIELD: {
												if (formFieldData === null) {
													storeField.setValue(null, 'dateTime');
													break;
												}

												storeField.setValue(formFieldData, 'dateTime');

												break;
											}
											case RICH_TEXT_GQL_FIELD: {
												if (formFieldData === null) {
													storeField.setValue(null, 'richText');
													return;
												}
												const richTextDataId = uuid();
												const adfDataId = `${richTextDataId}:adfValue`;
												const plainTextDataId = `${adfDataId}:convertedPlainText`;
												const plainTextRecord = store.create(
													plainTextDataId,
													'JiraAdfToConvertedPlainText',
												);
												plainTextRecord.setValue(convertAdfToText(formFieldData), 'plainText');
												const adfRecord = store.create(`${richTextDataId}:adfValue`, 'JiraADF');
												adfRecord.setLinkedRecord(plainTextRecord, 'convertedPlainText');
												const richTextRecord = store.create(richTextDataId, 'JiraRichText');
												richTextRecord.setLinkedRecord(adfRecord, 'adfValue');
												storeField.setLinkedRecord(richTextRecord, 'richText');
												break;
											}
											case STATUS_GQL_FIELD: {
												const statusCategory = store.create(uuid(), 'JiraStatusCategory');
												statusCategory.setValue(
													formFieldData.to.statusCategory.statusCategoryId,
													'statusCategoryId',
												);
												const status = store.create(uuid(), 'JiraStatus');
												status.setValue(formFieldData.to.statusId, 'statusId');
												status.setValue(formFieldData.to.name, 'name');
												status.setLinkedRecord(statusCategory, 'statusCategory');
												storeField.setLinkedRecord(status, 'status');
												break;
											}
											case AFFECTED_SERVICES_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;

												const multiSelectFieldOption = extraData?.[field.fieldId];

												runMultiSelectOptions(
													multiSelectFieldOption,
													formFieldData,
													storeField,
													store,
													{
														entityConnection: {
															name: 'selectedAffectedServicesConnection',
														},
														entityType: 'JiraAffectedService',
														values: {
															name: 'label',
															serviceId: 'value',
														},
													},
												);

												break;
											}
											case CMDB_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;

												const multiSelectFieldOption = extraData?.[field.fieldId];

												if (
													multiSelectFieldOption === 'ADD' ||
													multiSelectFieldOption === 'REPLACE'
												) {
													const entityConnectionRecord = storeField.getLinkedRecord(
														'selectedCmdbObjectsConnection',
													);
													if (!entityConnectionRecord) return;

													// eslint-disable-next-line @typescript-eslint/no-explicit-any
													const newEdges = formFieldData.map((entityData: any) => {
														const entity = store.create(uuid(), 'JiraCmdbObject');
														Object.entries({
															objectId: 'objectId',
															avatar: 'avatar',
															label: 'label',
														}).forEach(([key, value]) => {
															if (key === 'avatar') {
																const avatar = store.create(uuid(), 'JiraCmdbAvatar');
																const mediaClientConfig = store.create(
																	uuid(),
																	'JiraCmdbMediaClientConfig',
																);

																mediaClientConfig.setValue(
																	formFieldData[0]?.avatar.mediaClientConfig.mediaBaseUrl,
																	'mediaBaseUrl',
																);
																mediaClientConfig.setValue(
																	formFieldData[0]?.avatar.mediaClientConfig.mediaJwtToken,
																	'mediaJwtToken',
																);
																mediaClientConfig.setValue(
																	formFieldData[0]?.avatar.mediaClientConfig.fileId,
																	'fileId',
																);
																mediaClientConfig.setValue(
																	formFieldData[0]?.avatar.mediaClientConfig.clientId,
																	'clientId',
																);
																avatar.setLinkedRecord(mediaClientConfig, 'mediaClientConfig');
																avatar.setValue(formFieldData[0]?.avatar.url48, 'url48');
																entity.setLinkedRecord(avatar, 'avatar');
															} else {
																entity.setValue(entityData?.[value], key);
															}
														});

														const edge = store.create(uuid(), 'JiraCmdbObjectEdge');
														edge.setLinkedRecord(entity, 'node');
														return edge;
													});
													const existingEdges: RecordProxy<{}>[] =
														entityConnectionRecord.getLinkedRecords('edges') || [];
													const combinedEdges = [
														...(multiSelectFieldOption === 'ADD' ? existingEdges : []),
														...newEdges,
													];

													const deDuplicateCmdbEdges = (edges: RecordProxy<{}>[]) => {
														const seenLabels = new Set<string>();
														const uniqueEdges = edges.filter((edge) => {
															const label = String(
																edge?.getLinkedRecord('node')?.getValue('label'),
															);
															if (seenLabels.has(label)) return false;
															seenLabels.add(label);
															return true;
														});

														return uniqueEdges.sort((edgeA, edgeB) => {
															const labelA = String(
																edgeA?.getLinkedRecord('node')?.getValue('label'),
															);
															const labelB = String(
																edgeB?.getLinkedRecord('node')?.getValue('label'),
															);
															return labelA.localeCompare(labelB);
														});
													};

													const uniqueEdges = deDuplicateCmdbEdges(combinedEdges);

													updateRecords(entityConnectionRecord, uniqueEdges);
												} else if (multiSelectFieldOption === 'REMOVE') {
													const entityConnectionRecord = storeField.getLinkedRecord(
														'selectedCmdbObjectsConnection',
													);
													if (!entityConnectionRecord) return;

													const existingEdges: RecordProxy<{}>[] =
														entityConnectionRecord.getLinkedRecords('edges') || [];
													const entitiesToRemove = formFieldData.map(
														(entityData: { label: string }) => entityData.label,
													);
													const updatedEdges = existingEdges.filter((edge) => {
														const entityName = edge?.getLinkedRecord('node')?.getValue('label');
														return !entitiesToRemove.includes(entityName);
													});

													updateRecords(entityConnectionRecord, updatedEdges);

													break;
												} else {
													runMultiSelectOptions(
														multiSelectFieldOption,
														formFieldData,
														storeField,
														store,
														{
															entityConnection: {
																name: 'selectedCmdbObjectsConnection',
															},
															entityType: 'JiraCmdbObject',
														},
													);
												}
												break;
											}
											// TODO: currently the fields below render as fallback HTML in NIN
											// this means we can't implement optimistic updates
											// we will come back to this when the field is rendered properly in NIN
											// Read more: https://hello.atlassian.net/wiki/spaces/Spork/pages/5568381338/NIN+fields+that+use+fallback+HTML+on+FE
											case RESPONDERS_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;
												break;
											}
											case PEOPLE_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;
												break;
											}
											case FORGE_GROUP_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;
												break;
											}
											case FORGE_GROUPS_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;
												break;
											}
											case FORGE_NUMBER_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;
												break;
											}
											case FORGE_STRINGS_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;
												break;
											}
											case FORGE_OBJECT_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;
												break;
											}
											case FORGE_STRING_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;
												break;
											}
											case FORGE_USER_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;
												break;
											}
											case FORGE_USERS_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;
												break;
											}
											case FLAG_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;

												const flagDataId = `${uuid()}:flag`;
												const flagRecord = store.create(flagDataId, 'JiraFlag');
												flagRecord.setValue(!!formFieldData?.[0], 'isFlagged');

												storeField.setLinkedRecord(flagRecord, 'flag');

												break;
											}
											case FORGE_DATETIME_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;
												break;
											}
											case ISSUE_LINK_GQL_FIELD: {
												if (!expVal('sunsetting_bulk_edit', 'isEnabled', false)) break;
												break;
											}
											default:
												break;
										}
									}

									/*
									We explicitly check for type on certain instances due to the face that the
									main_IssueNavigator query has the __typename of JiraFallbackField even though
									this is not accurate.
								*/
									const __issueAriIdFallback = JiraIssuefieldvalueAri.create({
										siteId,
										issueId: issueId.toString(),
										fieldId: `${field.fieldId}_fallback`,
									}).toString();
									const relayDataIdFallback = RelayDataID(
										{
											id: __issueAriIdFallback,
										},
										field.gqlType,
									);

									if (relayDataIdFallback) {
										const storeFieldFallback = store.get(relayDataIdFallback);

										if (storeFieldFallback) {
											switch (field.type) {
												case MULTI_CHECKBOXES_CF_TYPE: {
													const renderedFieldId = `${field.fieldId}-field`;
													const renderedFieldHtml = renderToString(
														<div id={renderedFieldId}>
															{/*  eslint-disable-next-line @typescript-eslint/consistent-type-assertions */}
															{(formFieldData as { label: string }[]).map((labelData, index) => (
																<React.Fragment key={`${renderedFieldId}-${labelData.label}`}>
																	<span>{labelData.label}</span>
																	{index !== formFieldData.length - 1 && ', '}
																</React.Fragment>
															))}
														</div>,
													);

													storeFieldFallback.setValue(renderedFieldHtml, 'renderedFieldHtml');
													break;
												}
												case USER_CF_TYPE: {
													if (formFieldData === null) {
														storeFieldFallback.setValue('', 'renderedFieldHtml');
														return;
													}
													const renderedFieldId = `${field.fieldId}-field`;
													const renderedFieldHtml = renderToString(
														<div id={renderedFieldId}>
															{fg('replace-anchor-with-link__deliveroo') ? (
																<Link
																	id={renderedFieldId}
																	href={`/secure/ViewProfile.jspa?accountId=${formFieldData.id}`}
																>
																	{formFieldData.name}
																</Link>
															) : (
																// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
																<a
																	id={renderedFieldId}
																	href={`/secure/ViewProfile.jspa?accountId=${formFieldData.id}`}
																>
																	{formFieldData.name}
																</a>
															)}
														</div>,
													);

													storeFieldFallback.setValue(renderedFieldHtml, 'renderedFieldHtml');
													break;
												}
												default:
													break;
											}
										}
									}
								}
							});
						} else if (bulkOperationType === 'BULK_WATCH' || bulkOperationType === 'BULK_UNWATCH') {
							ariIssueIds.forEach((ariIssueId) => {
								const { siteId, issueId } = JiraIssueAri.parse(ariIssueId);
								const ariIssueFieldId = JiraIssuefieldvalueAri.create({
									siteId,
									issueId,
									fieldId: field.fieldId,
								}).toString();
								const relayDataId = RelayDataID(
									{
										id: ariIssueFieldId,
									},
									field.gqlType,
								);
								if (relayDataId) {
									const storeField = store.get(relayDataId);
									if (storeField) {
										switch (field.gqlType) {
											case WATCHES_GQL_FIELD: {
												const currentWatch = storeField.getLinkedRecord('watch');
												const currentCount = currentWatch?.getValue('count');
												const currentIsWatching = currentWatch?.getValue('isWatching');

												if (currentCount != null && currentIsWatching != null) {
													const watch = store.create(uuid(), 'JiraWatch');
													let count = Number(currentCount);

													if (bulkOperationType === 'BULK_WATCH' && !currentIsWatching) {
														count++;
														watch.setValue(count, 'count');
														watch.setValue(true, 'isWatching');
														storeField.setLinkedRecord(watch, 'watch');
														break;
													} else if (
														bulkOperationType === 'BULK_UNWATCH' &&
														currentIsWatching &&
														count > 0
													) {
														count--;
														watch.setValue(count, 'count');
														watch.setValue(false, 'isWatching');
														storeField.setLinkedRecord(watch, 'watch');
														break;
													}
												}
												break;
											}
											default:
												break;
										}
									}
								}
							});
						}
					});
				});
			} catch (e: unknown) {
				log.safeErrorWithoutCustomerData(
					'bulk-operations.async.full-core.onUpdate',
					e instanceof Error ? e.toString() : 'unknown error',
				);
			}
		},
		[issueFields],
	);
	return onUpdate;
};
